import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import type { ThemeRow, ThemeTokens } from "@cashsync/shared";

const MIN_REQUIRED_GLOWS = 3;

const isStringArray = (arr: unknown): arr is string[] =>
  Array.isArray(arr) && arr.every((item) => typeof item === "string");

const parseGlowColors = (raw: string | null | undefined): string[] | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isStringArray(parsed) && parsed.length > 0) {
      return parsed;
    }
  } catch {
    // Ignore parsing errors for invalid raw JSON strings, falling back safely to null
  }
  return null;
};

/** Main pages navigable via swipe/shortcuts, in sidebar order. */
const ORDERED_PAGE_PATHS = [
  "/",
  "/transactions",
  "/accounts",
  "/categories",
  "/reports",
  "/calendar",
  "/budgets",
  "/recurring",
  "/projects",
  "/debts",
  "/data",
] as const;

export const getPageNameForPath = (path: string, t: (msg: MessageDescriptor) => string) => {
  switch (path) {
    case "/": {
      return t(msg`Dashboard`);
    }
    case "/transactions": {
      return t(msg`Transactions`);
    }
    case "/accounts": {
      return t(msg`Accounts`);
    }
    case "/categories": {
      return t(msg`Categories`);
    }
    case "/reports": {
      return t(msg`Reports`);
    }
    case "/calendar": {
      return t(msg`Calendar`);
    }
    case "/budgets": {
      return t(msg`Budgets`);
    }
    case "/recurring": {
      return t(msg`Recurring`);
    }
    case "/projects": {
      return t(msg`Projects`);
    }
    case "/debts": {
      return t(msg`Debts`);
    }
    case "/data": {
      return t(msg`Data`);
    }
    default: {
      return t(msg`Page`);
    }
  }
};

export const getGlowColors = (globalTheme: ThemeRow | null, globalToks: ThemeTokens | null) => {
  if (globalTheme) {
    const glowArray = parseGlowColors(globalTheme.glow_colors);
    if (glowArray && glowArray.length >= MIN_REQUIRED_GLOWS) {
      return {
        glow0: glowArray[0],
        glow1: glowArray[1],
        glow2: glowArray[2],
      };
    }
  }

  if (globalToks && globalToks.id !== "none") {
    return {
      glow0: globalToks.glow0,
      glow1: globalToks.glow1,
      glow2: globalToks.glow2,
    };
  }

  return { glow0: "var(--glow-0)", glow1: "var(--glow-1)", glow2: "var(--glow-2)" };
};

export const getOrderedPagePaths = (): string[] => [...ORDERED_PAGE_PATHS];
